import type {
  ComputedExpression,
  ConditionExpression,
  ExpressionNode,
  FieldRef,
  NumberLiteral,
  StringLiteral,
} from './MasterDocumentTemplate.types';

// ════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════

function isFieldRef(node: unknown): node is FieldRef {
  return typeof node === 'object' && node !== null && 'field' in node && !('op' in node) && !('value' in node);
}

function isLiteral(node: unknown): node is NumberLiteral | StringLiteral {
  return typeof node === 'object' && node !== null && 'value' in node && !('op' in node) && !('field' in node);
}

function toNumber(val: unknown): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const n = Number(val);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

// ════════════════════════════════════════════════════════════
// RESOLVE NODE — Resuelve un nodo a su valor
// ════════════════════════════════════════════════════════════

function resolveNode(
  node: ExpressionNode | StringLiteral,
  data: Record<string, unknown>,
  customEvaluator?: (ruleId: string, formData: Record<string, unknown>) => unknown,
): unknown {
  if (isFieldRef(node)) return data[node.field];
  if (isLiteral(node)) return node.value;
  return evaluateExpression(node as ComputedExpression, data, customEvaluator);
}

// ════════════════════════════════════════════════════════════
// EVALUATE CONDITION
// ════════════════════════════════════════════════════════════

export function evaluateCondition(
  cond: ConditionExpression,
  data: Record<string, unknown>,
  customEvaluator?: (ruleId: string, formData: Record<string, unknown>) => unknown,
): boolean {
  switch (cond.op) {
    case 'eq':
      return resolveNode(cond.left, data, customEvaluator) === resolveNode(cond.right, data, customEvaluator);
    case 'neq':
      return resolveNode(cond.left, data, customEvaluator) !== resolveNode(cond.right, data, customEvaluator);
    case 'gt':
      return toNumber(resolveNode(cond.left, data, customEvaluator)) > toNumber(resolveNode(cond.right, data, customEvaluator));
    case 'gte':
      return toNumber(resolveNode(cond.left, data, customEvaluator)) >= toNumber(resolveNode(cond.right, data, customEvaluator));
    case 'lt':
      return toNumber(resolveNode(cond.left, data, customEvaluator)) < toNumber(resolveNode(cond.right, data, customEvaluator));
    case 'lte':
      return toNumber(resolveNode(cond.left, data, customEvaluator)) <= toNumber(resolveNode(cond.right, data, customEvaluator));
    case 'and':
      return cond.conditions.every((c) => evaluateCondition(c, data, customEvaluator));
    case 'or':
      return cond.conditions.some((c) => evaluateCondition(c, data, customEvaluator));
    case 'not':
      return !evaluateCondition(cond.condition, data, customEvaluator);
  }
}

// ════════════════════════════════════════════════════════════
// EVALUATE EXPRESSION — Motor principal
// ════════════════════════════════════════════════════════════

export function evaluateExpression(
  expr: ComputedExpression,
  data: Record<string, unknown>,
  customEvaluator?: (ruleId: string, formData: Record<string, unknown>) => unknown,
): unknown {
  switch (expr.op) {
    case 'add':
      return expr.args.reduce(
        (sum, arg) => sum + toNumber(resolveNode(arg, data, customEvaluator)),
        0,
      );

    case 'subtract': {
      const [a, b] = expr.args;
      return toNumber(resolveNode(a, data, customEvaluator)) - toNumber(resolveNode(b, data, customEvaluator));
    }

    case 'multiply':
      return expr.args.reduce(
        (product, arg) => product * toNumber(resolveNode(arg, data, customEvaluator)),
        1,
      );

    case 'divide': {
      const [num, den] = expr.args;
      const d = toNumber(resolveNode(den, data, customEvaluator));
      return d === 0 ? 0 : toNumber(resolveNode(num, data, customEvaluator)) / d;
    }

    case 'round': {
      const [val, decimals] = expr.args;
      const v = toNumber(resolveNode(val, data, customEvaluator));
      const dec = decimals.value;
      const factor = 10 ** dec;
      return Math.round(v * factor) / factor;
    }

    case 'coalesce': {
      for (const arg of expr.args) {
        const v = resolveNode(arg, data, customEvaluator);
        if (v != null && v !== '' && v !== 0) return v;
      }
      return 0;
    }

    case 'if': {
      const passes = evaluateCondition(expr.condition, data, customEvaluator);
      return resolveNode(passes ? expr.then : expr.else, data, customEvaluator);
    }

    case 'custom': {
      if (customEvaluator) {
        return customEvaluator(expr.ruleId, data);
      }
      return undefined;
    }
  }
}

// ════════════════════════════════════════════════════════════
// EVALUATE NODE — Entry point público para nodos genéricos
// ════════════════════════════════════════════════════════════

export function evaluateNode(
  node: ExpressionNode,
  data: Record<string, unknown>,
  customEvaluator?: (ruleId: string, formData: Record<string, unknown>) => unknown,
): unknown {
  if (isFieldRef(node)) return data[node.field];
  if (isLiteral(node)) return node.value;
  return evaluateExpression(node as ComputedExpression, data, customEvaluator);
}
